export function renderBuildDeleteDialogSourceClientModuleTemplate({
  deleteActionName,
  actionImportPath,
  componentName,
  quoteLiteral,
  queryKey,
  quoteLiteralQuotedValue,
  quoteLiteralText,
  escapeJsx,
  quoteLiteralContent
}: {
  deleteActionName: string
  actionImportPath: string
  componentName: string
  quoteLiteral: string
  queryKey: string
  quoteLiteralQuotedValue: string
  quoteLiteralText: string
  escapeJsx: string
  quoteLiteralContent: string
}): string {
  return `'use client'

import { ${deleteActionName} } from '@admin/actions/${actionImportPath}'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@admin/components/ui/alert-dialog'
import { DropdownMenuItem } from '@admin/components/ui/dropdown-menu'
import { useQueryClient } from '@tanstack/react-query'
import { Trash } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'

interface ${componentName}Props {
  id: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ${componentName}({
  id,
  open,
  onOpenChange
}: ${componentName}Props) {
  const [isPending, startTransition] = React.useTransition()
  const queryClient = useQueryClient()

  const handleDelete = () => {
    startTransition(async () => {
      try {
        const result = await ${deleteActionName}(id)

        if (result.success) {
          toast.success(${quoteLiteral})
          queryClient.refetchQueries({ queryKey: ['${queryKey}'] })
          onOpenChange(false)
        } else {
          toast.error(result.error || ${quoteLiteralQuotedValue})
        }
      } catch (error) {
        toast.error(${quoteLiteralText})
        console.error(error)
      }
    })
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogTrigger
        render={
          <DropdownMenuItem
            variant="destructive"
            closeOnClick={false}
            onClick={(event) => event.preventDefault()}
          >
            <Trash className="size-4" />
            Delete ${escapeJsx}
          </DropdownMenuItem>
        }
      />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete ${escapeJsx}?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes it and cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              handleDelete()
            }}
            disabled={isPending}
            aria-busy={isPending}
          >
            {isPending ? 'Deleting…' : ${quoteLiteralContent}}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
`
}
